import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { type ScheduleItem } from '@/lib/scheduleUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  items: ScheduleItem[];
  filterTitleSearch: string;
  onSetFilterTitleSearch: (v: string) => void;
  filterAssignedTo: string;
  onSetFilterAssignedTo: (v: string) => void;
  filterStatus: 'all' | 'unfinished' | 'completed' | 'delayed' | 'at_risk';
  onSetFilterStatus: (v: 'all' | 'unfinished' | 'completed' | 'delayed' | 'at_risk') => void;
  filterPhase: string;
  onSetFilterPhase: (v: string) => void;
  filterDateRange: 'all' | 'this_week' | 'this_month' | 'custom';
  onSetFilterDateRange: (v: 'all' | 'this_week' | 'this_month' | 'custom') => void;
  filterCustomStart: string;
  onSetFilterCustomStart: (v: string) => void;
  filterCustomEnd: string;
  onSetFilterCustomEnd: (v: string) => void;
  showExcluded: boolean;
  onToggleShowExcluded: () => void;
  onApply: () => void;
  onClearAll: () => void;
}

const STAGE_OPTIONS = ['Planning', 'Design', 'Consent', 'Construction', 'Handover'];

export default function ScheduleFilterDrawer({
  isOpen,
  onClose,
  items,
  filterTitleSearch,
  onSetFilterTitleSearch,
  filterAssignedTo,
  onSetFilterAssignedTo,
  filterStatus,
  onSetFilterStatus,
  filterPhase,
  onSetFilterPhase,
  filterDateRange,
  onSetFilterDateRange,
  filterCustomStart,
  onSetFilterCustomStart,
  filterCustomEnd,
  onSetFilterCustomEnd,
  showExcluded,
  onToggleShowExcluded,
  onApply,
  onClearAll,
}: Props) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);

  const STATUS_OPTIONS: { value: 'all' | 'unfinished' | 'completed' | 'delayed' | 'at_risk'; label: string; color: string }[] = [
    { value: 'all', label: t('scheduleFilter.statusAll'), color: '#6B7280' },
    { value: 'unfinished', label: t('scheduleFilter.statusUnfinished'), color: '#F59E0B' },
    { value: 'completed', label: t('scheduleFilter.statusCompleted'), color: '#059669' },
    { value: 'delayed', label: t('scheduleFilter.statusDelayed'), color: '#EF4444' },
    { value: 'at_risk', label: t('scheduleFilter.statusAtRisk'), color: '#F97316' },
  ];

  // Collect unique phases and assignees from items
  const { phases, assignees } = useMemo(() => {
    const phaseSet = new Set<string>();
    const assigneeSet = new Set<string>();
    for (const item of items) {
      if (item.phase) phaseSet.add(item.phase);
      if (item.assigned_to) assigneeSet.add(item.assigned_to);
      if (item.assigned_role && item.assigned_role !== 'all') assigneeSet.add(item.assigned_role);
    }
    return {
      phases: Array.from(phaseSet).sort(),
      assignees: Array.from(assigneeSet).sort(),
    };
  }, [items]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasActiveFilters =
    filterTitleSearch.trim() !== '' ||
    filterAssignedTo !== 'all' ||
    filterStatus !== 'all' ||
    filterPhase !== 'all' ||
    filterDateRange !== 'all' ||
    showExcluded;

  return (
    <div className="fixed inset-0 z-[150] flex justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        ref={panelRef}
        className="relative z-10 w-[380px] max-w-full bg-white shadow-2xl h-full flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#F9FAFB] flex items-center justify-center">
              <i className="ri-filter-3-line text-[#374151] text-sm"></i>
            </div>
            <h2 className="text-sm font-bold text-[#1A1A1A]">{t('scheduleFilter.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer transition-colors"
            aria-label={t('scheduleFilter.title')}
          >
            <i className="ri-close-line text-[#6B7280]"></i>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Title search */}
          <div>
            <label className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
              {t('scheduleFilter.searchTitle')}
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-xs"></i>
              <input
                type="text"
                value={filterTitleSearch}
                onChange={(e) => onSetFilterTitleSearch(e.target.value)}
                placeholder={t('scheduleFilter.searchPlaceholder')}
                className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] placeholder:text-[#D1D5DB] focus:outline-none focus:border-[#E8621A] focus:ring-1 focus:ring-[#E8621A]/20"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
              {t('scheduleFilter.status')}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onSetFilterStatus(opt.value)}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    filterStatus === opt.value
                      ? 'text-white'
                      : 'bg-gray-50 text-[#6B7280] hover:bg-gray-100'
                  }`}
                  style={
                    filterStatus === opt.value
                      ? { backgroundColor: opt.color }
                      : undefined
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stage */}
          <div>
            <label className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
              {t('scheduleFilter.stage')}
            </label>
            <select
              value={filterPhase !== 'all' ? filterPhase : 'all'}
              onChange={(e) => onSetFilterPhase(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] cursor-pointer focus:outline-none focus:border-[#E8621A] focus:ring-1 focus:ring-[#E8621A]/20"
            >
              <option value="all">{t('scheduleFilter.allStages')}</option>
              {STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
              {phases.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          {/* Assigned To */}
          {assignees.length > 0 && (
            <div>
              <label className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
                {t('scheduleFilter.assignedTo')}
              </label>
              <select
                value={filterAssignedTo}
                onChange={(e) => onSetFilterAssignedTo(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] cursor-pointer focus:outline-none focus:border-[#E8621A] focus:ring-1 focus:ring-[#E8621A]/20"
              >
                <option value="all">{t('scheduleFilter.all')}</option>
                {assignees.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Date range */}
          <div>
            <label className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
              {t('scheduleFilter.dateRange')}
            </label>
            <select
              value={filterDateRange}
              onChange={(e) =>
                onSetFilterDateRange(e.target.value as 'all' | 'this_week' | 'this_month' | 'custom')
              }
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] cursor-pointer focus:outline-none focus:border-[#E8621A] focus:ring-1 focus:ring-[#E8621A]/20"
            >
              <option value="all">{t('scheduleFilter.allDates')}</option>
              <option value="this_week">{t('scheduleFilter.thisWeek')}</option>
              <option value="this_month">{t('scheduleFilter.thisMonth')}</option>
              <option value="custom">{t('scheduleFilter.customRange')}</option>
            </select>

            {filterDateRange === 'custom' && (
              <div className="flex gap-2 mt-2">
                <input
                  type="date"
                  value={filterCustomStart}
                  onChange={(e) => onSetFilterCustomStart(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] focus:outline-none focus:border-[#E8621A]"
                />
                <span className="flex items-center text-xs text-[#9CA3AF]">{t('scheduleFilter.to')}</span>
                <input
                  type="date"
                  value={filterCustomEnd}
                  onChange={(e) => onSetFilterCustomEnd(e.target.value)}
                  className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs text-[#374151] focus:outline-none focus:border-[#E8621A]"
                />
              </div>
            )}
          </div>

          {/* Show excluded toggle */}
          <div className="flex items-center justify-between">
            <label className="text-xs text-[#374151] font-medium">{t('scheduleFilter.showExcluded')}</label>
            <button
              onClick={onToggleShowExcluded}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${
                showExcluded ? 'bg-[#E8621A]' : 'bg-gray-200'
              }`}
              aria-label={t('scheduleFilter.showExcluded')}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  showExcluded ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-4 border-t border-gray-200 flex items-center gap-2">
          <button
            onClick={() => {
              onClearAll();
              onClose();
            }}
            disabled={!hasActiveFilters}
            className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-[#6B7280] cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            {t('scheduleFilter.clearAll')}
          </button>
          <button
            onClick={() => {
              onApply();
              onClose();
            }}
            className="flex-1 px-3 py-2 rounded-lg bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors"
          >
            {t('scheduleFilter.applyFilter')}
          </button>
        </div>
      </div>
    </div>
  );
}